import React, { useEffect, useState } from "react";
import { CheckSquare, ListChecks, Square } from "lucide-react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const CrmPlaybookPanel = ({ leadId, compact = false, collapsible = false }) => {
  const [open, setOpen] = useState(false);
  const [playbook, setPlaybook] = useState([]);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    if (!leadId) return;
    if (collapsible && !open) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${url}/leads/intelligence/${leadId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (cancelled || !res) return;
        setPlaybook(res.playbook || []);
        setDone(Array.isArray(res.playbookProgress) ? res.playbookProgress.map(String) : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, url, token, collapsible, open]);

  const toggleStep = async (stepId) => {
    if (!leadId || saving) return;
    const normalizedId = String(stepId);
    const isDone = done.map(String).includes(normalizedId);
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/playbook-progress/${leadId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: normalizedId, done: !isDone }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setDone(Array.isArray(data.playbookProgress) ? data.playbookProgress.map(String) : []);
    } catch {
      toast.error("Could not update checklist");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) return null;

  if (collapsible) {
    if (open && loading) {
      return (
        <div className="crm-playbook crm-playbook--collapsible">
          <button
            type="button"
            className="crm-playbook__toggle is-open"
            onClick={() => setOpen(false)}
            aria-expanded="true"
          >
            <ListChecks size={14} />
            <span>Play Stage Playbook</span>
          </button>
          <div className="crm-playbook__body">
            <p className="crm-insights__muted">Loading playbook…</p>
          </div>
        </div>
      );
    }

    if (!open) {
      return (
        <div className="crm-playbook crm-playbook--collapsible">
          <button
            type="button"
            className="crm-playbook__toggle"
            onClick={() => setOpen(true)}
            aria-expanded="false"
          >
            <ListChecks size={14} />
            <span>Play Stage Playbook</span>
          </button>
        </div>
      );
    }

    if (!playbook.length) {
      return (
        <div className="crm-playbook crm-playbook--collapsible">
          <button
            type="button"
            className="crm-playbook__toggle is-open"
            onClick={() => setOpen(false)}
            aria-expanded="true"
          >
            <ListChecks size={14} />
            <span>Play Stage Playbook</span>
          </button>
          <div className="crm-playbook__body">
            <p className="crm-insights__muted">No playbook steps for this stage.</p>
          </div>
        </div>
      );
    }
  } else {
    if (loading) return null;
    if (!playbook.length) return null;
  }

  const completed = done.filter((id) => playbook.some((s) => String(s.id) === String(id))).length;

  const checklist = (
    <ul className="crm-playbook__list">
      {playbook.map((step) => {
        const checked = done.map(String).includes(String(step.id));
        return (
          <li key={step.id}>
            <button
              type="button"
              className={`crm-playbook__step${checked ? " is-done" : ""}`}
              onClick={() => toggleStep(step.id)}
              disabled={saving}
            >
              {checked ? <CheckSquare size={16} /> : <Square size={16} />}
              <span>{step.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (collapsible) {
    return (
      <div className="crm-playbook crm-playbook--collapsible">
        <button
          type="button"
          className="crm-playbook__toggle is-open"
          onClick={() => setOpen(false)}
          aria-expanded="true"
        >
          <ListChecks size={14} />
          <span>Stage playbook</span>
          <span className="crm-playbook__toggle-count">
            {completed}/{playbook.length}
          </span>
        </button>
        <div className="crm-playbook__body">{checklist}</div>
      </div>
    );
  }

  return (
    <div className={`crm-playbook${compact ? " crm-playbook--compact" : ""}`}>
      <div className="crm-playbook__head">
        <strong>Stage playbook</strong>
        <span className="crm-insights__muted">
          {completed}/{playbook.length}
        </span>
      </div>
      {checklist}
    </div>
  );
};

export default CrmPlaybookPanel;
